import { useCallback, useEffect, useRef, useState } from "react";
import type { FunnelState as EngineState } from "@nextday/funnel-engine";

/**
 * Survey-kit-driven funnels record answers as a flat string-keyed map
 * (the renderer's `answerKey` → answer value). The shape is intentionally
 * generic so the draft hook stays brand-agnostic — see
 * `apps/funnel/src/funnel/Funnel.tsx`.
 */
export type FunnelDraftAnswers = Record<string, unknown>;

/**
 * localStorage-backed draft persistence for the funnel SPA.
 *
 * Scope (Phase 1 close-out, NX-9281):
 *   - Per-browser only. Cross-device resume is Phase 3 (remote draft via
 *     funnel-api draft endpoints).
 *   - Survives reload, tab close/reopen. Cleared on completion or TTL expiry.
 *   - Schema-versioned: bumping `DRAFT_SCHEMA_VERSION` invalidates old drafts
 *     so we never restore state into an incompatible engine.
 *
 * Non-goals:
 *   - Encryption / PII protection (the answers contain only first name + age
 *     band + preferences; nothing more sensitive than what the user just typed).
 *   - Conflict resolution with a remote draft.
 */

const DRAFT_SCHEMA_VERSION = 1;
const DRAFT_TTL_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

type FunnelDraft = {
  schemaVersion: number;
  savedAt: number;
  engineState: EngineState;
  answers: FunnelDraftAnswers;
  /** Fingerprint of the funnel config at save time. Drafts with a mismatched
   *  fingerprint are discarded so stale sessions don't crash on schema changes. */
  configFingerprint?: string;
};

const buildKey = (funnelId: string, overrideId: string | null): string =>
  `nextday:funnel-draft:${funnelId}:${overrideId ?? "_base"}`;

const isFunnelDraft = (candidate: unknown): candidate is FunnelDraft => {
  if (typeof candidate !== "object" || candidate === null) return false;
  const draft = candidate as Partial<FunnelDraft>;
  return (
    draft.schemaVersion === DRAFT_SCHEMA_VERSION &&
    typeof draft.savedAt === "number" &&
    typeof draft.engineState === "object" &&
    draft.engineState !== null &&
    typeof draft.answers === "object" &&
    draft.answers !== null
  );
};

/** Simple fingerprint: sorted step IDs joined with their types. Changes when
 *  steps are added, removed, reordered, or change type. */
export const computeConfigFingerprint = (
  stepIds: ReadonlyArray<{ id: string; type: string }>,
): string => stepIds.map((s) => `${s.id}:${s.type}`).join("|");

const safeReadDraft = (key: string, currentFingerprint?: string): FunnelDraft | null => {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    if (!isFunnelDraft(parsed)) return null;
    if (Date.now() - parsed.savedAt > DRAFT_TTL_MS) {
      window.localStorage.removeItem(key);
      return null;
    }
    // Discard drafts saved against a different funnel config.
    if (
      currentFingerprint &&
      parsed.configFingerprint &&
      parsed.configFingerprint !== currentFingerprint
    ) {
      window.localStorage.removeItem(key);
      return null;
    }
    return parsed;
  } catch {
    // Quota errors, malformed JSON, disabled storage in private mode — all OK.
    return null;
  }
};

const safeWriteDraft = (key: string, draft: FunnelDraft): void => {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(key, JSON.stringify(draft));
  } catch {
    // Storage quota exceeded or disabled — silently degrade. Never block the
    // user's funnel progression because of a draft-save failure.
  }
};

const safeClearDraft = (key: string): void => {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.removeItem(key);
  } catch {
    // ignore
  }
};

export type FunnelDraftHook = {
  /** Initial draft restored on mount; `null` if no valid draft existed. */
  initialDraft: FunnelDraft | null;
  /** Persist the latest engine + answers snapshot. Debounced internally. */
  saveDraft: (engineState: EngineState, answers: FunnelDraftAnswers) => void;
  /** Wipe the saved draft. Call on funnel completion. */
  clearDraft: () => void;
};

export const useFunnelDraft = (params: {
  funnelId: string;
  overrideId: string | null;
  /** Step shapes for fingerprinting. When provided, stale drafts from a
   *  different config version are automatically discarded. */
  steps?: ReadonlyArray<{ id: string; type: string }>;
}): FunnelDraftHook => {
  const key = buildKey(params.funnelId, params.overrideId);
  const fingerprint = params.steps ? computeConfigFingerprint(params.steps) : undefined;

  /**
   * Resolve the draft once on mount. We use a lazy initializer so the
   * localStorage read happens exactly once even under React strict mode's
   * double-render in development.
   */
  const [initialDraft] = useState<FunnelDraft | null>(() => safeReadDraft(key, fingerprint));

  // Debounce writes so rapid state churn (e.g. typing in TextShort) doesn't
  // hammer localStorage. 250ms is well under perceived "this should already be
  // saved" while batching keystrokes within a single step.
  const writeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingDraftRef = useRef<FunnelDraft | null>(null);
  /**
   * Once `clearDraft` runs (funnel completion), this hook becomes inert: any
   * subsequent `saveDraft` call from a trailing render or the unmount flush is
   * a no-op. This prevents the email-included answers re-rendering the parent
   * and resurrecting the draft after we explicitly wiped it.
   */
  const clearedRef = useRef(false);

  const flushPendingWrite = useCallback((): void => {
    if (clearedRef.current) {
      pendingDraftRef.current = null;
      writeTimerRef.current = null;
      return;
    }
    if (pendingDraftRef.current) {
      safeWriteDraft(key, pendingDraftRef.current);
      pendingDraftRef.current = null;
    }
    writeTimerRef.current = null;
  }, [key]);

  const saveDraft = useCallback(
    (engineState: EngineState, answers: FunnelDraftAnswers): void => {
      if (clearedRef.current) return;
      pendingDraftRef.current = {
        schemaVersion: DRAFT_SCHEMA_VERSION,
        savedAt: Date.now(),
        engineState,
        answers,
        configFingerprint: fingerprint,
      };
      if (writeTimerRef.current !== null) {
        clearTimeout(writeTimerRef.current);
      }
      writeTimerRef.current = setTimeout(flushPendingWrite, 250);
    },
    [flushPendingWrite],
  );

  const clearDraft = useCallback((): void => {
    clearedRef.current = true;
    if (writeTimerRef.current !== null) {
      clearTimeout(writeTimerRef.current);
      writeTimerRef.current = null;
    }
    pendingDraftRef.current = null;
    safeClearDraft(key);
  }, [key]);

  // Flush on unmount so reloads don't drop a half-typed answer that was within
  // the debounce window. Skipped if the draft was explicitly cleared.
  useEffect(
    () => () => {
      if (writeTimerRef.current !== null) {
        clearTimeout(writeTimerRef.current);
        flushPendingWrite();
      }
    },
    [flushPendingWrite],
  );

  return { initialDraft, saveDraft, clearDraft };
};
